/**
 * Redirects `getDisplayMedia` to the NATIVE source picker.
 *
 * Patching the platform call rather than changing every capture site is the
 * whole trick: `features/meeting/media/localMedia.ts`, the voice room and any
 * future surface all keep calling `getDisplayMedia` exactly as they do in a
 * browser, and in the desktop client they transparently get the real picker
 * plus a `chromeMediaSourceId` stream.
 *
 * Installed once, guarded, and a no-op outside the desktop shell.
 */
import { hasEchoCancelledSystemAudio, isDesktopShell } from './bridge';
import { requestScreenSource } from './screenSources';

/** Chromium's legacy desktop-capture constraint. Not in the DOM lib types. */
interface DesktopCaptureConstraint {
  mandatory: {
    chromeMediaSource: 'desktop';
    chromeMediaSourceId: string;
    maxFrameRate?: number;
    maxWidth?: number;
    maxHeight?: number;
  };
}

/**
 * SYSTEM AUDIO, the only way Chromium offers it to a desktop capture.
 *
 * Deliberately WITHOUT `chromeMediaSourceId`: Chromium's loopback capturer is
 * whole-device, so pairing it with a window's source id is what makes the whole
 * `getUserMedia` call fail rather than what scopes it. It works on Windows,
 * where WASAPI loopback exists; on Linux it throws (there is no such capturer)
 * and on macOS it is not implemented at all. Both of those land in the retry
 * below, which is why this is attempted rather than assumed.
 *
 * Worth being plain about: on Windows this is the WHOLE machine's output, not
 * the shared window's — and that output includes THIS CALL. Every other
 * participant's voice comes out of the presenter's speakers and goes back to
 * them in the share's soundtrack, which is the echo people heard on every
 * desktop share while this was asked for by default. It is now reached only
 * when the presenter turns on "Include computer sound" in the share picker,
 * which warns about exactly that (`ShareStartOptions`); a request with
 * `audio: false` never opens the loopback capturer.
 */
const DESKTOP_AUDIO = { mandatory: { chromeMediaSource: 'desktop' } };

/**
 * COMPUTER SOUND WITHOUT THE CALL IN IT — desktop 0.1.1 and later, Windows.
 *
 * The loopback above can only be taken raw: Chromium will not run its echo
 * canceller on a legacy desktop capture (asked for, it reports
 * `echoCancellation: false` and stays that way). `getDisplayMedia` CAN, and the
 * canceller's reference is exactly the call audio this app is playing — so the
 * other participants' voices are taken back out of the soundtrack while a
 * video's sound, which this app is not playing, goes through. Measured with
 * Electron 33 on Windows: the call fell from -29 dB to about -60 dB in the
 * capture within a second; another application's sound lost under 2 dB.
 *
 * Noise suppression and auto gain stay OFF: they belong on a voice, never on a
 * soundtrack. This path reaches the main process's display-media handler,
 * which shows the same source picker and answers with the loopback because the
 * page asked for sound (apps/desktop/src/shared/systemAudio.ts).
 */
const ECHO_CANCELLED_SOUND: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: false,
  autoGainControl: false,
};

let installed = false;

/**
 * Reads one numeric ceiling out of a `getDisplayMedia` constraint.
 *
 * The desktop capturer understands only the legacy `mandatory` block, so every
 * limit the caller expressed the modern way has to be translated by hand — and
 * anything not translated is simply lost. That is why the resolution cap
 * `localMedia` sets must be forwarded here too: without it the desktop client
 * keeps capturing the panel's native retina size and the share stays soft
 * however carefully the browser path is tuned.
 */
function ceilingOf(
  constraints: DisplayMediaStreamOptions | undefined,
  key: 'frameRate' | 'width' | 'height',
): number | undefined {
  const video = constraints?.video;
  if (typeof video !== 'object' || video === null) return undefined;
  const value = (video as MediaTrackConstraints)[key];
  if (typeof value === 'number') return value;
  if (typeof value === 'object' && value !== null) {
    return value.max ?? value.ideal ?? undefined;
  }
  return undefined;
}

/** Only defined keys may enter `mandatory`; an `undefined` there is a failure. */
const optional = (key: string, value: number | undefined): Record<string, number> =>
  value === undefined ? {} : { [key]: value };

export function installDesktopScreenShare(): void {
  if (installed || !isDesktopShell()) return;
  if (typeof navigator === 'undefined' || navigator.mediaDevices === undefined) return;
  installed = true;

  const devices = navigator.mediaDevices;
  const original = devices.getDisplayMedia.bind(devices);

  devices.getDisplayMedia = async (
    constraints?: DisplayMediaStreamOptions,
  ): Promise<MediaStream> => {
    const wantsSound = constraints?.audio !== undefined && constraints.audio !== false;
    if (wantsSound && hasEchoCancelledSystemAudio()) {
      /* ONE picker: the main process asks this page for the source, exactly
         as the requestScreenSource path below does. A cancel arrives as
         `AbortError`, which every Share path already reads as "cancelled". */
      return original({ video: constraints?.video ?? true, audio: ECHO_CANCELLED_SOUND });
    }

    const sourceId = await requestScreenSource();
    if (sourceId === null) {
      /* Same error a browser raises when its own chooser is dismissed, so the
         existing "Screen sharing was cancelled" handling keeps working. */
      throw new DOMException('Screen share was cancelled.', 'NotAllowedError');
    }

    const capture: DesktopCaptureConstraint = {
      mandatory: {
        chromeMediaSource: 'desktop',
        chromeMediaSourceId: sourceId,
        ...optional('maxFrameRate', ceilingOf(constraints, 'frameRate')),
        ...optional('maxWidth', ceilingOf(constraints, 'width')),
        ...optional('maxHeight', ceilingOf(constraints, 'height')),
      },
    };

    /* Chromium-only shape; the DOM typings cannot express it, and a guard
       cannot narrow a constraint object the platform never declares. */
    const video = capture as unknown as MediaTrackConstraints;

    if (wantsSound) {
      /* SOUND IS A BONUS, NEVER A CONDITION. Whether this platform has a
         loopback capturer is not something the caller should have to know, and
         a share that fails because the machine cannot send its own audio would
         be a worse feature than the silent one this replaces. No second picker:
         the source id is already in hand, so the retry is invisible. */
      const withAudio = await devices
        .getUserMedia({ audio: DESKTOP_AUDIO as unknown as MediaTrackConstraints, video })
        .catch(() => null);
      if (withAudio !== null) return withAudio;
    }

    try {
      return await devices.getUserMedia({ audio: false, video });
    } catch {
      /* The source vanished between the picker and the capture (window closed,
         display unplugged). Fall back to the platform chooser rather than
         failing the share outright. */
      return original(constraints);
    }
  };
}
